/**
 * Invitation Management API
 * Frontend interface for invitation operations
 */

import { getCurrentUser } from '@aws-amplify/auth';
import { apiClient } from './apiClient';

class InviteAPI {
  constructor() {
    // No configuration needed - apiClient handles everything
  }

  // No custom auth headers needed when using Amplify REST API with Cognito authorizer

  /**
   * Get current user's information
   */
  async getCurrentUserInfo() {
    try {
      const user = await getCurrentUser();
      const { fetchUserAttributes } = await import('@aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      return {
        cognitoSub: user.userId,
        email: attributes.email,
        name: attributes.given_name || attributes.email
      };
    } catch (error) {
      console.error('Error getting current user info:', error);
      return { cognitoSub: null, email: null, name: null };
    }
  }

  /**
   * Create a new invitation
   */
  async createInvite(inviteData) {
    try {
      const userInfo = await this.getCurrentUserInfo();
      
      const data = await apiClient.invites({
        operation: 'createInvite',
        ...inviteData,
        inviterUserId: userInfo.cognitoSub,
        inviterEmail: userInfo.email
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to create invitation');
      }

      return data;
    } catch (error) {
      console.error('Error creating invite:', error);
      throw error;
    }
  }

  /**
   * Create multiple invitations in a single request
   * Returns warnings listing emails of existing users that were skipped
   */
  async createInvites(invites, defaults = {}) {
    try {
      const userInfo = await this.getCurrentUserInfo();
      const payload = {
        operation: 'createInvites',
        invites,
        ...defaults,
        inviterUserId: userInfo.cognitoSub,
        inviterEmail: userInfo.email
      };

      const data = await apiClient.invites(payload);

      if (!data.success) {
        throw new Error(data.error || 'Failed to create invitations');
      }

      return data.data; // { createdCount, results, warnings, skippedExisting, failures }
    } catch (error) {
      console.error('Error creating invites:', error);
      throw error;
    }
  }

  /**
   * Get invitation status by token
   */
  async getInviteStatus(token) {
    try {
      const data = await apiClient.invites({
        operation: 'getInviteStatus',
        token
      });

      // Normalize shape from backend (supports both {valid:true} and {status:"valid"})
      const isValid = data?.valid === true || data?.status === 'valid';
      if (!isValid) {
        // When backend returns success:false or explicit error
        const errMsg = data?.error || 'Invalid or expired invitation';
        return { valid: false, error: errMsg };
      }

      return { valid: true };
    } catch (error) {
      console.error('Error getting invite status:', error);
      throw error;
    }
  }

  /**
   * Accept an invitation (used during registration flow)
   */
  async acceptInvite(inviteId, token = null) {
    try {
      const userInfo = await this.getCurrentUserInfo();
      const data = await apiClient.invites({
        operation: 'acceptInvite',
        inviteId,
        cognitoSub: userInfo.cognitoSub,
        token
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to accept invitation');
      }

      return data.data;
    } catch (error) {
      console.error('Error accepting invite:', error);
      throw error;
    }
  }

  /**
   * Revoke an invitation
   */
  async revokeInvite(inviteId) {
    try {
      const userInfo = await this.getCurrentUserInfo();
      
      const data = await apiClient.invites({
        operation: 'revokeInvite',
        inviteId,
        revokedBy: userInfo.cognitoSub
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to revoke invitation');
      }

      return data.data;
    } catch (error) {
      console.error('Error revoking invite:', error);
      throw error;
    }
  }

  /**
   * List invitations for a clinic
   */
  async listInvites(clinicId, status = null) {
    try {
      const data = await apiClient.invites({
        operation: 'listInvites',
        clinicId,
        status
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to list invitations');
      }

      return data.data;
    } catch (error) {
      console.error('Error listing invites:', error);
      throw error;
    }
  }

  /**
   * Resend invitation email
   */
  async resendInvite(inviteId) {
    try {
      const userInfo = await this.getCurrentUserInfo();
      
      const data = await apiClient.invites({
        operation: 'resendInvite',
        inviteId,
        requestedBy: userInfo.cognitoSub
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to resend invitation');
      }

      return data.data;
    } catch (error) {
      console.error('Error resending invite:', error);
      throw error;
    }
  }

  /**
   * Clean up expired invitations
   */
  async cleanupExpiredInvites() {
    try {
      const data = await apiClient.invites({
        operation: 'cleanupExpiredInvites'
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to cleanup invitations');
      }

      return data.data;
    } catch (error) {
      console.error('Error cleaning up invites:', error);
      throw error;
    }
  }

  /**
   * Validate invitation token (for public invite acceptance page)
   */
  async validateInvite(token) {
    try {
      const data = await apiClient.invites({
        operation: 'validateInvite',
        token
      });

      if (!data.success) {
        throw new Error(data.error || 'Invalid invitation');
      }

      return data.data;
    } catch (error) {
      console.error('Error validating invite:', error);
      throw error;
    }
  }

  /**
   * Get valid invitations by email (used by Post-Confirmation trigger)
   */
  async getValidInvitesByEmail(email) {
    try {
      const data = await apiClient.invites({
        operation: 'getValidInvitesByEmail',
        email
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to get invitations');
      }

      return data.data;
    } catch (error) {
      console.error('Error getting invites by email:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const inviteAPI = new InviteAPI();
export default inviteAPI;
